/* eslint-disable @typescript-eslint/no-use-before-define */
import { AppNavParams } from '@euler/app/Routes';
import { Typography } from '@euler/app/components/typography';
import { useServiceRecordContext } from '@euler/app/flows/record/ServiceRecordContext';
import { ProductManifestProps } from '@euler/app/flows/report/types';
import { FaultInspectionInfoList } from '@euler/app/flows/task/pages/detail/components/FaultInspectionInfo';
import { TaskDetailSection } from '@euler/app/flows/task/pages/detail/components/TaskDetailSection';
import { isFaultLikeServiceSubject } from '@euler/functions/isFaultLikeServiceSubject';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { InspectionTaskJobStatus } from '@euler/model/enum';
import { TaskProductManifestItem } from '@euler/model/task-detail';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';

export const FaultInspections = memo(() => {
  const { serviceRecord, reportTarget } = useServiceRecordContext();
  const { rpx } = useResponsiveContext();

  const items = useMemo<FaultInspectionItem[]>(() => {
    const faultLikeSubjects =
      serviceRecord.orderDetail?.serviceSubjects?.filter(
        isFaultLikeServiceSubject,
      ) ?? [];
    return faultLikeSubjects.map<FaultInspectionItem>(subject => ({
      type: subject.type,
      title: subject.name,
      relatedInspections: subject.relatedInspections,
      relatedJobs: subject.relatedJobs?.map(relatedJob => ({
        ...relatedJob,
        jobStatus: InspectionTaskJobStatus.Finished,
      })),
      remark: subject.remark,
      unsolvedReason: subject.unsolvedReason,
      note: subject.note,
      assignedTo: subject.assignedTo,
      status: subject.status,
    }));
  }, [serviceRecord.orderDetail?.serviceSubjects]);

  const navigation = useNavigation<StackNavigationProp<AppNavParams>>();

  const productManifest$ = useCreation(
    () =>
      new BehaviorSubject(
        serviceRecord.productReferrals?.map<TaskProductManifestItem>(x => ({
          id: x.id,
          author: x.author,
          product: x.product,
          qty: x.qty ?? 1,
          subject: x.subject,
          jobRefs: x.jobRefs,
          origin: x.origin!,
          confirmations: x.confirmations,
        })) ?? [],
      ),
    [serviceRecord.productReferrals],
  );

  const productManifestProps = useMemo<ProductManifestProps>(() => {
    return {
      items$: productManifest$,
      onViewDetail: item => {
        navigation.push('_productDetail', {
          id: item.product.id,
        });
      },
    };
  }, [navigation, productManifest$]);

  const onViewItemInspectionDetails = usePersistFn(
    async (item: FaultInspectionItem) => {
      navigation.push('inspectionReport', {
        standalone: true,
        target: reportTarget,
        title: `${item.title}-检测结果`,
        faultLikeSubject: item,
        ...{
          productManifest: productManifestProps,
        },
      });
    },
  );

  const onViewItemConstructionDetails = usePersistFn(
    async (item: FaultInspectionItem) => {
      navigation.push('constructionReport', {
        standalone: true,
        target: reportTarget,
        title: `${item.title}-施工反馈`,
        faultLikeSubject: item,
        ...{
          productManifest: productManifestProps,
        },
      });
    },
  );

  if (!items.length) {
    return null;
  }

  return (
    <>
      <TaskDetailSection
        title="故障检修"
        headerRight={
          <Typography.Text light size={rpx(12)} color="#777">
            {items.filter(x => x.status === 'solved').length} / {items.length}{' '}
          </Typography.Text>
        }
        bodyStyle={{
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        <FaultInspectionInfoList
          items={items}
          scene="service-record"
          onViewInspectionDetails={onViewItemInspectionDetails}
          onViewJobDetails={onViewItemConstructionDetails}
        />
      </TaskDetailSection>
    </>
  );
});
